import {saveAs} from 'file-saver'
import {chargeBackHost} from 'GLOBAL/envVariables'
import {axiosWithAuth} from 'helper/axiosWithAuth'
import {Download, FileSpreadsheet, FolderDown, Search, Settings, Upload, X} from 'lucide-react'
import {useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {programNameHandler} from '../func/datePickerInputFormat'
import tabs from '../func/tabs'
import ChargeDatePicker from './ChargeDatePicker'
import MultiSelect from './MultiSelect'
import ProgramsDataGrid from './ProgramsDataGrid'

import swal from 'sweetalert'
import {savedFailAlert, showDateRequiredAlert, showNoDataAlert} from 'utils/CustomAlert/Alert'
import {
  befUploadStatus,
  checkDayStatus,
  dataUploadStatus,
  fileMimeMap,
  officeDownloadFormatMap,
} from '../func/whetherStatus'
import CheckDayMultiSelect from './CheckDayMultiSelect'
import FileUpload from './FileUpload'

interface RowData {
  customer_number: string
  name: string
  address: string
  [key: string]: any
}

const TabMenu = () => {
  const officeInfo = useSelector((state: any) => state?.officeInfo)
  const userInfo = useSelector((state: any) => state.userInfo?.userInfo)
  const officeValue = useSelector((state: any) => state?.officeInfo.value)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState('정상')
  const [searchText, setSearchText] = useState('')
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [programName, setProgramName] = useState<string>('')
  const [data, setData] = useState<RowData[]>([])
  const [filteredData, setFilteredData] = useState<RowData[]>([])
  const [loadingTarget, setLoadingTarget] = useState<null | 'excel' | 'upload' | 'save' | 'move'>(
    null
  )
  const [selectedOptionValues, setSelectedOptionValues] = useState<string[]>([])
  const [selectedCooperOptionValues, setSelectedCooperOptionValues] = useState<string[]>([])
  const [resolvedDataFromChild, setResolvedDataFromChild] = useState<RowData[]>([])
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [uploadType, setUploadType] = useState<'before' | 'info'>('before')
  const [isSaved, setIsSaved] = useState(false)
  const [selectedCheckDays, setSelectedCheckDays] = useState<string[]>([])
  const [selectedIds, setSelectedIds] = useState<any[]>([])
  const [selectionResetKey, setSelectionResetKey] = useState(0)

  const isUploadEnabled = (type: 'before' | 'info') => {
    const status = type === 'before' ? befUploadStatus : dataUploadStatus
    return status.find((item) => item.office === officeValue)?.whether ?? false
  }

  useEffect(() => {
    setProgramName(programNameHandler(officeValue))
  }, [officeValue])

  useEffect(() => {
    setSearchText('')
    setFilteredData(data)
    setIsSaved(false)
    setSelectedIds([])
    setSelectionResetKey((k) => k + 1)
  }, [
    activeTab,
    selectedDate,
    officeValue,
    selectedCheckDays,
    selectedOptionValues,
    selectedCooperOptionValues,
  ])

  const tabList = tabs({
    chargeBackHost,
    officeValue,
    programName,
    checkDate: selectedDate,
  })

  const fetchTabData = async () => {
    const targetTab = tabList.find((tab) => tab.label === activeTab)
    if (!targetTab) return

    try {
      const url = new URL(targetTab.endpoint)

      if (selectedOptionValues.length > 0 && !selectedOptionValues.includes('all')) {
        url.searchParams.append('option', selectedOptionValues.join(','))
      }

      if (selectedCooperOptionValues.length > 0 && !selectedCooperOptionValues.includes('all')) {
        url.searchParams.append('cooper', selectedCooperOptionValues.join(','))
      }

      if (
        checkDayStatus[officeValue] &&
        selectedCheckDays.length > 0 &&
        !selectedCheckDays.includes('all')
      ) {
        url.searchParams.append('check_day', selectedCheckDays.join(','))
      }

      const response = await axiosWithAuth().get(url.toString())
      setData(response.data)
      setFilteredData(response.data)

      setSelectedIds([])
      setSelectionResetKey((k) => k + 1)
    } catch (error) {
      console.error('API 요청 실패:', error)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsExportDropdownOpen(false)
      }
    }

    if (isExportDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isExportDropdownOpen])

  useEffect(() => {
    if (activeTab && selectedDate && officeValue) {
      fetchTabData()
    }
  }, [
    activeTab,
    selectedDate,
    officeValue,
    selectedOptionValues,
    selectedCheckDays,
    selectedCooperOptionValues,
  ])

  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredData(data)
    }
  }, [searchText, data])

  const handleSearch = () => {
    const keyword = searchText.trim().toLowerCase()
    if (!keyword) {
      setFilteredData(data)
      return
    }
    const result = data.filter(
      (item) =>
        item.customer_number?.toLowerCase().includes(keyword) ||
        item.customer_name?.toLowerCase().includes(keyword) ||
        item.address_road?.toLowerCase().includes(keyword)
    )
    setFilteredData(result)
  }

  const handlerSave = async () => {
    if (resolvedDataFromChild.length === 0) {
      showNoDataAlert()
      return
    }

    setLoadingTarget('save')

    const filteredData = resolvedDataFromChild.filter((item: any) => {
      if (activeTab === '확인필요' && item.edited_status === false) {
        const dayFromIdx = item.idx_date?.substring(6, 8)
        return String(item.check_day).padStart(2, '0') === dayFromIdx
      }
      return true
    })

    const checkValueData = filteredData.map((item: any) => {
      item.check_value = item?.check_value === null ? item?.check_value_default : item?.check_value
      item.check_user = userInfo.userName

      const excludeOffices = [
        'A800',
        '4700',
        '5701',
        'H101',
        'A600',
        '1900',
        '7601',
        '7602',
        '3801',
        '8601',
        '7001',
      ]

      if (!excludeOffices.includes(officeValue)) {
        item.now_used =
          item?.check_value === ''
            ? ''
            : item?.now_used === null ||
              item?.check_value !== null ||
              item?.bef_check_value !== null
            ? Number(item?.check_value) - Number(item?.bef_check_value)
            : item?.now_used

        const bef_used = Number(item.bef_used)
        const leak_average = bef_used * 1.2 < Number(item.now_used)
        item.leak_status = leak_average === true ? true : false
      } else {
        item.now_used = null
        item.leak_status = false
      }

      const excludeChargeStatuses = ['NO_BEF', 'MINUS', 'BEF_ABNORMAL', 'BEF_DAY', 'AFT_DAY']

      if (item.edited_status === true && !excludeChargeStatuses.includes(item.charge_status)) {
        item.charge_status = 'NORMAL'
      }
      if (item.now_used !== null && !isNaN(item.now_used) && Number(item.now_used) < 0) {
        item.charge_status = 'MINUS'
      }

      return item
    })

    try {
      if (activeTab !== '제외수용가') {
        await axiosWithAuth().post(
          `${chargeBackHost}/api/save?office_code=${officeValue}&check_date=${selectedDate}&company=${programName}`,
          {checkValueData}
        )

        await swal({
          title: '성공',
          text: '저장이 완료되었습니다.',
          icon: 'success',
          buttons: [false],
          timer: 2000,
        })
        fetchTabData()
        setIsSaved(true)
      } else {
        await swal({
          title: '실패',
          text: '저장할 수 없는 탭입니다.',
          buttons: [false],
          icon: 'error',
          timer: 1500,
        })
      }
      setLoadingTarget(null)
    } catch (error) {
      console.error('저장실패:', error)
    }
  }

  const checkFileDownload = async () => {
    const skipSaveCheck = activeTab === '제외수용가'

    if (!skipSaveCheck && isSaved === false) {
      await savedFailAlert()
      return false
    }

    setLoadingTarget('excel')

    const params = new URLSearchParams()
    params.append('office_code', officeValue)
    params.append('check_date', selectedDate || '')
    params.append('company', programName)
    params.append('check_user', userInfo?.userName || '')

    if (selectedOptionValues.length > 0 && !selectedOptionValues.includes('all')) {
      params.append('option', selectedOptionValues.join(','))
    }

    if (selectedCooperOptionValues.length > 0 && !selectedCooperOptionValues.includes('all')) {
      params.append('cooper', selectedCooperOptionValues.join(','))
    }

    try {
      const res = await axiosWithAuth().post(
        `${chargeBackHost}/down/excel/check?${params.toString()}`,
        {data},
        {responseType: 'arraybuffer'}
      )

      if (res.status === 200) {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })

        const fileName = `${officeInfo?.label.replace(' ', '_')}_${selectedDate} (확인용).xlsx`
        saveAs(blob, fileName)

        await swal({
          title: '성공',
          text: '성공적으로 처리되었습니다.',
          icon: 'success',
          buttons: [false],
          timer: 2000,
        })
        setIsSaved(false)
      }
    } catch (err) {
      console.error('파일 다운로드 실패:', err)
      await swal({
        title: '실패',
        text: '다운로드에 실패하였습니다.',
        buttons: [false],
        icon: 'error',
        timer: 1500,
      })
    } finally {
      setLoadingTarget(null)
    }
  }

  const resultFileDownload = async () => {
    if (isSaved === false) {
      await savedFailAlert()
      return false
    }
    setLoadingTarget('upload')

    const params = new URLSearchParams()
    params.append('office_code', officeValue)
    params.append('check_date', selectedDate || '')
    params.append('company', programName)

    try {
      const savedRows = resolvedDataFromChild.filter((r) => r.check_user !== null)

      const res = await axiosWithAuth().post(
        `${chargeBackHost}/down/result?${params.toString()}`,
        {data: savedRows},
        {
          responseType: 'arraybuffer',
        }
      )

      if (res.status === 200) {
        const ext = officeDownloadFormatMap[officeValue] || ''
        const mimeType = fileMimeMap[ext]

        const blob = new Blob([res.data], {type: mimeType})
        let fileName = ''

        if (
          officeValue === 'C100' ||
          officeValue === '0801' ||
          officeValue === '7900' ||
          officeValue.includes('Z1') ||
          officeValue.includes('08') ||
          officeValue === 'B700'
        ) {
          fileName = `RstG40.${ext}`
        } else {
          fileName = `${officeInfo?.label}_${selectedDate} (업로드용).${ext}`
        }

        saveAs(blob, fileName)

        await swal({
          title: '성공',
          text: '성공적으로 처리되었습니다.',
          icon: 'success',
          buttons: [false],
          timer: 2000,
        })
        setIsSaved(false)
      }
    } catch (err) {
      console.error('파일 다운로드 실패:', err)
      await swal({
        title: '실패',
        text: '다운로드에 실패하였습니다.',
        buttons: [false],
        icon: 'error',
        timer: 1500,
      })
    } finally {
      setLoadingTarget(null)
    }
  }

  const handleSampleDownload = async () => {
    const params = new URLSearchParams()
    params.append('office_code', officeValue)

    const endpoint = `${chargeBackHost}/sample?${params.toString()}`
    const fileName = `${officeInfo.label}_등록정보_샘플.xlsx`

    try {
      const res = await axiosWithAuth().post(endpoint, {data: data}, {responseType: 'blob'})

      if (res.status === 200) {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        saveAs(blob, fileName)
      }
    } catch (error) {
      console.error('샘플 파일 다운로드 실패:', error)
      await swal({
        title: '실패',
        text: '샘플 파일을 가져오는 데 실패했습니다.',
        icon: 'error',
        buttons: [false],
        timer: 1500,
      })
    }
  }

  const isAnyUploadEnabled =
    befUploadStatus.find((item) => item.office === officeValue)?.whether ||
    dataUploadStatus.find((item) => item.office === officeValue)?.whether

  useEffect(() => {
    const befEnabled = befUploadStatus.find((item) => item.office === officeValue)?.whether
    const dataEnabled = dataUploadStatus.find((item) => item.office === officeValue)?.whether

    if (befEnabled && !dataEnabled) {
      setUploadType('before')
    } else if (!befEnabled && dataEnabled) {
      setUploadType('info')
    }
  }, [officeValue])

  const handleMove = async () => {
    if (selectedIds.length === 0) {
      swal({
        title: '알림',
        text: '선택된 행이 없습니다.',
        icon: 'warning',
        buttons: [false],
        timer: 1500,
      })
      return
    }

    const selectedRows = resolvedDataFromChild.filter((_, idx) => selectedIds.includes(idx))

    try {
      setLoadingTarget('move')
      const res = await axiosWithAuth().post(`${chargeBackHost}/api/move`, {
        office_code: officeValue,
        check_date: selectedDate,
        company: programName,
        movedData: selectedRows.map((row) => ({
          id: row.id,
          check_user: userInfo.userName,
          customer_number: row.customer_number,
          charge_status: 'MOVE',
        })),
      })

      if (res.data.success) {
        await swal({
          title: '성공',
          text: `${res.data.count}건이 이동 처리되었습니다.`,
          icon: 'success',
          buttons: [false],
          timer: 2000,
        })

        setSelectedIds([])
        setSelectionResetKey((k) => k + 1)
        fetchTabData()
      } else {
        await swal({
          title: '실패',
          text: '이동 처리 중 오류가 발생했습니다.',
          icon: 'error',
          buttons: [false],
          timer: 2000,
        })
      }
    } catch (err) {
      console.error('이동 실패:', err)
      swal({
        title: '실패',
        text: '이동 요청 중 오류가 발생했습니다.',
        icon: 'error',
        buttons: [false],
        timer: 2000,
      })
    } finally {
      setLoadingTarget(null)
    }
  }

  const handleGoSettings = () => {
    navigate('/charge/setting')
  }

  return (
    <div className={`card w-full p-4 h-full ${loadingTarget !== null ? 'cursor-progress' : ''}`}>
      <div className='flex flex-col text-lg md:flex-row items-center justify-between border-b border-gray-300 pb-2'>
        <div className='flex flex-nowrap overflow-x-auto space-x-3 mb-3 md:mb-0 items-center'>
          {tabList.map((tab) => (
            <button
              key={tab.label}
              className={`whitespace-nowrap px-4 py-2 text-lg md:text-xl font-medium transition-all ${
                activeTab === tab.label
                  ? 'border-b-2 border-blue-500 text-blue-500'
                  : 'text-gray-500 hover:text-blue-500'
              }`}
              onClick={() => setActiveTab(tab.label)}
            >
              {tab.label}
            </button>
          ))}

          {userInfo?.accountType === 'sys_admin' && (
            <button
              type='button'
              className='whitespace-nowrap px-4 py-2 text-lg md:text-xl font-medium transition-all text-gray-500 hover:text-blue-500 flex items-center gap-2'
              onClick={handleGoSettings}
            >
              <Settings className='w-6 h-6' />
              설정
            </button>
          )}
        </div>

        <div className='flex flex-wrap items-center justify-center md:justify-end gap-3'>
          <ChargeDatePicker
            state={selectedDate}
            setState={setSelectedDate}
            programName={programName}
            officeValue={officeValue}
          />
          {checkDayStatus[officeValue] && (
            <CheckDayMultiSelect officeValue={officeValue} onChange={setSelectedCheckDays} />
          )}

          <MultiSelect officeValue={officeValue} onChange={setSelectedOptionValues} type='option' />
          <MultiSelect
            officeValue={officeValue}
            onChange={setSelectedCooperOptionValues}
            type='cooper'
          />

          <div className='relative w-full sm:w-64 md:w-80'>
            <input
              type='text'
              placeholder='검색'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
              className='w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'
            />
            <Search
              className='absolute right-9 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 cursor-pointer'
              onClick={handleSearch}
            />
            {searchText && (
              <X
                className='absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 hover:text-gray-600 cursor-pointer'
                onClick={() => setSearchText('')}
              />
            )}
          </div>
          <button
            onClick={handlerSave}
            className='px-4 py-3 sm:px-3 sm:py-2 text-lg text-green-700 bg-green-200 rounded-md hover:bg-green-300 font-bold'
          >
            저장
          </button>

          {activeTab === '확인필요' && (
            <button
              onClick={handleMove}
              disabled={selectedIds.length === 0}
              className={`px-4 py-3 sm:px-3 sm:py-2 text-lg font-bold rounded-md ${
                selectedIds.length === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-200 text-indigo-700 hover:bg-indigo-300'
              }`}
            >
              이동
            </button>
          )}

          {isAnyUploadEnabled && (
            <button
              onClick={() => {
                if (!selectedDate) {
                  showDateRequiredAlert()
                  return
                }
                setIsImportModalOpen(true)
              }}
              className='flex items-center text-blue-700 bg-blue-200 hover:bg-blue-300 px-4 py-3 rounded-lg transition font-bold'
            >
              <Upload className='w-5 h-5 mr-2' />
              Import
            </button>
          )}
          {isImportModalOpen && (
            <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20'>
              <div className='bg-white w-[90%] max-w-lg p-6 rounded-xl shadow-lg'>
                <div className='flex justify-between items-center mb-4'>
                  <h2 className='text-xl font-bold'>데이터 업로드</h2>
                  <button
                    onClick={() => setIsImportModalOpen(false)}
                    className='text-gray-500 hover:text-red-500 text-2xl'
                  >
                    &times;
                  </button>
                </div>

                <div className='mb-4'>
                  {befUploadStatus.find((item) => item.office === officeValue)?.whether && (
                    <label className='flex items-center gap-2 mb-2'>
                      <input
                        type='checkbox'
                        name='uploadType'
                        value='before'
                        checked={uploadType === 'before'}
                        onChange={() => setUploadType('before')}
                      />
                      전월자료 업로드
                    </label>
                  )}

                  {dataUploadStatus.find((item) => item.office === officeValue)?.whether && (
                    <label className='flex items-center gap-2'>
                      <input
                        type='checkbox'
                        name='uploadType'
                        value='info'
                        checked={uploadType === 'info'}
                        onChange={() => setUploadType('info')}
                      />
                      등록정보 업로드
                    </label>
                  )}
                </div>

                <div className='space-y-4'>
                  {isUploadEnabled(uploadType) && (
                    <FileUpload
                      uploadUrl={`${chargeBackHost}/upload/${
                        uploadType === 'before' ? 'before' : 'info'
                      }?office_code=${officeValue}&check_date=${selectedDate}&company=${programName}&user=${
                        userInfo.userId
                      }`}
                      onSuccess={() => {
                        setIsImportModalOpen(false)
                        fetchTabData()
                      }}
                      uploadType={uploadType}
                    />
                  )}
                </div>

                <div
                  className={`mt-6 text-right flex ${
                    uploadType === 'info' ? 'justify-between' : 'justify-end'
                  }`}
                >
                  {uploadType === 'info' &&
                    dataUploadStatus.find((item) => item.office === officeValue)?.whether && (
                      <button
                        onClick={handleSampleDownload}
                        className='px-3 py-2 text-sm bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition'
                      >
                        샘플 양식 다운로드
                      </button>
                    )}

                  <button
                    onClick={() => setIsImportModalOpen(false)}
                    className='px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded'
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          )}
          <div ref={dropdownRef} className='relative'>
            <button
              onClick={() => {
                if (!selectedDate) {
                  showDateRequiredAlert()
                  return
                }
                setIsExportDropdownOpen((prev) => !prev)
              }}
              className='flex items-center text-purple-700 bg-purple-200 hover:bg-purple-300 px-4 py-3 rounded-lg transition font-bold'
              disabled={loadingTarget !== null}
            >
              <Download className='w-5 h-5 mr-2' />
              Export
            </button>

            {isExportDropdownOpen && (
              <div className='absolute right-0 mt-2 w-72 bg-white shadow-lg border rounded-xl z-50 p-2'>
                <button
                  onClick={checkFileDownload}
                  disabled={loadingTarget !== null}
                  className='flex items-center gap-2 w-full px-4 py-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <FileSpreadsheet className='w-4 h-4' />
                  확인용 엑셀 다운로드
                </button>

                <button
                  onClick={resultFileDownload}
                  disabled={loadingTarget !== null || activeTab !== '정상'}
                  className='flex items-center gap-2 w-full px-4 py-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <FolderDown className='w-4 h-4' />
                  업로드용 파일 다운로드
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='pt-4 text-xl flex justify-between items-center'>
        <span>
          <span className='text-blue-600 font-bold'>{officeInfo.label}</span>
          에서 요금연계를 진행합니다.
        </span>
        <span>
          총 전수 : <span className='text-blue-600 font-semibold'>{data.length}</span> 전
        </span>
      </div>

      <div className='mt-4 h-full'>
        <ProgramsDataGrid
          data={filteredData}
          checkDate={selectedDate}
          activeTab={activeTab}
          programName={programName}
          onResolvedDataChange={setResolvedDataFromChild}
          officeCode={officeInfo.value}
          onRowSelectChange={setSelectedIds}
          externalClearSignal={selectionResetKey}
        />
      </div>

      {loadingTarget && (
        <div className='fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50'>
          <div className='bg-white p-6 rounded-lg shadow-lg flex items-center gap-3'>
            <svg className='animate-spin h-6 w-6 text-blue-600' viewBox='0 0 24 24' fill='none'>
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <circle
                className='opacity-75'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
                strokeDasharray='80'
                strokeDashoffset='60'
                strokeLinecap='round'
              />
            </svg>
            <span className='text-lg font-semibold text-gray-700'>
              {loadingTarget === 'save'
                ? '저장 중입니다...'
                : loadingTarget === 'excel'
                ? '엑셀 다운로드 중입니다...'
                : loadingTarget === 'upload'
                ? '업로드용 파일 다운로드 중입니다...'
                : '처리 중입니다...'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default TabMenu
